import { useLayoutEffect, useRef, useState } from "react";
import { TableHeaderCategoryConfig } from "../tableContext/TableContext";
import {
  getTableColumnLayout,
  measureTableColumns,
  TableColumnMetrics,
  TableMeasuredCell,
} from "../helpers/tableColumnSizing";

export default function useTableColumnSizing(
  headers: TableHeaderCategoryConfig[],
  withActions: boolean,
  showFilters: boolean,
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState({
    widths: [] as number[],
    width: 0,
    fillerWidth: 0,
  });

  useLayoutEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    let frame = 0;
    let needsMeasurement = true;
    let lastWidth = -1;
    let metrics: TableColumnMetrics[] = [];
    let cells: TableMeasuredCell[] = [];
    let previousBadgeWidths: number[] = [];
    const count = headers.length + (withActions ? 1 : 0);
    const measure = () => {
      frame = 0;
      const contentChanged = needsMeasurement;
      if (needsMeasurement) {
        ({ metrics, cells } = measureTableColumns(root, count));
        needsMeasurement = false;
      }
      const rem =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 13;
      const canvas = document.createElement("canvas").getContext("2d");
      const header = root.querySelector("thead th");
      const style = getComputedStyle(header ?? root);
      if (canvas)
        canvas.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const availableWidth = Math.min(
        root.getBoundingClientRect().width,
        ...Array.from(root.querySelectorAll<HTMLElement>("[data-table-scroll]"))
          .map((element) => element.getBoundingClientRect().width)
          .filter((value) => value > 0),
      );
      const next = getTableColumnLayout({
        headers,
        metrics,
        availableWidth,
        rem,
        withActions,
        showFilters,
        measureLabel: (text) =>
          canvas?.measureText(text).width ?? text.length * rem * 0.6,
      });
      setLayout((previous) =>
        previous.width === next.width &&
        previous.fillerWidth === next.fillerWidth &&
        previous.widths.length === next.widths.length &&
        previous.widths.every(
          (value, index) => Math.abs(value - next.widths[index]) < 0.25,
        )
          ? previous
          : next,
      );
      const badgeWidths = metrics.map((metric, index) =>
        Math.floor(
          Math.min(
            Math.ceil(metric.badge),
            Math.max(0, next.widths[index] - metric.padding - metric.accessory),
          ),
        ),
      );
      if (
        contentChanged ||
        badgeWidths.some((value, index) => value !== previousBadgeWidths[index])
      ) {
        cells.forEach(({ element: cell, index, span }) => {
          if (!metrics[index] || span !== 1) return;
          const value = `${badgeWidths[index]}px`;
          if (cell.style.getPropertyValue("--table-badge-width") !== value) {
            cell.style.setProperty("--table-badge-width", value);
          }
        });
      }
      previousBadgeWidths = badgeWidths;
    };
    const schedule = (measureContent = false) => {
      needsMeasurement ||= measureContent;
      if (!frame) frame = requestAnimationFrame(measure);
    };
    measure();
    const resize = new ResizeObserver(() => {
      const width = root.getBoundingClientRect().width;
      if (lastWidth === width) return;
      lastWidth = width;
      schedule();
    });
    resize.observe(root);
    const withoutBadgeWidth = (style: string | null) =>
      (style ?? "")
        .replace(/--table-badge-width\s*:[^;]+;?/g, "")
        .replace(/\s/g, "");
    const mutation = new MutationObserver((records) => {
      // Colgroup updates are our output, not a change in the displayed content.
      if (
        records.some(({ target, type, attributeName, oldValue }) => {
          if (!(target instanceof Element)) return true;
          if (
            target.closest("colgroup") ||
            target.hasAttribute("data-table-layout")
          )
            return false;
          if (
            type === "attributes" &&
            attributeName === "style" &&
            target.hasAttribute("data-table-column")
          ) {
            return (
              withoutBadgeWidth(oldValue) !==
              withoutBadgeWidth(target.getAttribute("style"))
            );
          }
          return true;
        })
      ) {
        schedule(true);
      }
    });
    mutation.observe(root, {
      childList: true,
      characterData: true,
      subtree: true,
      attributes: true,
      attributeOldValue: true,
      attributeFilter: [
        "class",
        "style",
        "colspan",
        "rowspan",
        "data-table-badge-min-width",
      ],
    });
    const loaded = () => schedule(true);
    document.fonts?.addEventListener("loadingdone", loaded);
    root.addEventListener("load", loaded, true);
    return () => {
      cancelAnimationFrame(frame);
      resize.disconnect();
      mutation.disconnect();
      document.fonts?.removeEventListener("loadingdone", loaded);
      root.removeEventListener("load", loaded, true);
    };
  }, [headers, withActions, showFilters]);

  return { containerRef, ...layout };
}
